/** The month and year grids the session calendar drills up into.
 *
 * Split out of SessionPicker purely for length: after the Breeze backfill
 * took the archive from 25 sessions to 167, the picker grew a month grid
 * and a year grid and crossed the 400-line gate. Nothing here knows about
 * the popup - both take a list of sessions and report a pick.
 */

import type { ReactElement } from "react";
import { cn } from "@/lib/cn";
import { sessionMonths, sessionYears } from "@/lib/sessionCalendar";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** A 12-cell month grid, months with no archived session rendered dead —
 * the same rule DayCell follows, so a thin month reads as thin here too. */
export function MonthGrid({
  year,
  sessions,
  onPick,
  currentMonth,
}: Readonly<{
  year: number;
  sessions: string[];
  onPick: (m: number) => void;
  currentMonth: number;
}>): ReactElement {
  const has = sessionMonths(sessions, year);
  return (
    <div className="grid grid-cols-3 gap-1">
      {MONTH_NAMES.map((name, i) => {
        const m = i + 1;
        return (
          <button
            key={name}
            type="button"
            disabled={!has.has(m)}
            aria-label={`${name} ${String(year)}`}
            aria-current={m === currentMonth ? "true" : undefined}
            className={cn(
              "h-7 rounded-ctl text-[11px] transition-colors",
              m === currentMonth && has.has(m) && "bg-accent font-semibold text-on-accent",
              m !== currentMonth && has.has(m) && "text-ink hover:bg-surface-2",
              !has.has(m) && "cursor-default text-ink-muted/40",
            )}
            onClick={() => {
              onPick(m);
            }}
          >
            {name}
          </button>
        );
      })}
    </div>
  );
}

/** Only years the archive actually holds. Unlike months there is no value
 * in showing empty ones — a year with no sessions is not a gap in a
 * sequence the reader is scanning, it is simply not part of the archive. */
export function YearGrid({
  sessions,
  onPick,
  currentYear,
}: Readonly<{
  sessions: string[];
  onPick: (y: number) => void;
  currentYear: number;
}>): ReactElement {
  const years = sessionYears(sessions);
  return (
    <div className="grid grid-cols-3 gap-1">
      {years.map((y) => (
        <button
          key={y}
          type="button"
          aria-label={String(y)}
          aria-current={y === currentYear ? "true" : undefined}
          className={cn(
            "h-7 rounded-ctl text-[11px] tabular-nums transition-colors",
            y === currentYear
              ? "bg-accent font-semibold text-on-accent"
              : "text-ink hover:bg-surface-2",
          )}
          onClick={() => {
            onPick(y);
          }}
        >
          {y}
        </button>
      ))}
    </div>
  );
}
